import { getErrorTranslations } from "lib/i18n"
import {
  BoxIcon,
  CircleXIcon,
  EarthIcon,
  GoalIcon,
  MegaphoneIcon,
  WorkflowIcon,
  ZapIcon,
} from "lucide-react"
import { useMemo } from "react"
import { UseFormReturn } from "react-hook-form"
import {
  CampaignDetailResponseDataSchema,
  ChannelEnum,
  Errors,
  FlowEnum,
  LaunchCampaignSchema,
  validateEventHasAction,
} from "schema"
import { z } from "zod"
import { UpdateCampaignSchemaFrontend } from "./useCampaignForm"

const errorFieldsIconMap = {
  name: BoxIcon,
  defaultLanguage: EarthIcon,
  goals: GoalIcon,
  channel: MegaphoneIcon,
  templates: MegaphoneIcon,
  callbackUrl: MegaphoneIcon,
  trigger: ZapIcon,
  nodes: WorkflowIcon,
}

export function useLaunchTooltip(
  form: UseFormReturn<z.infer<typeof UpdateCampaignSchemaFrontend>>,
  campaign: z.infer<typeof CampaignDetailResponseDataSchema>,
) {
  const { errors, campaignPayload } = useMemo(
    function () {
      const formValues = form.getValues()
      const parsed = LaunchCampaignSchema.safeParse(formValues)
      const issues = parsed.error?.issues ?? []

      const errorTranslations = getErrorTranslations()

      const schemaErrors = issues.map((error) => ({
        icon:
          errorFieldsIconMap[error.path.at(0) as keyof typeof errorFieldsIconMap] ?? CircleXIcon,
        translation:
          errorTranslations[error.message as keyof typeof errorTranslations] ??
          errorTranslations.generic_error,
      }))

      // validateEventHasAction is checked directly because Zod refinements on chained
      // schemas are not reliably surfaced when other field-level errors are also present
      const nodeErrors = !validateEventHasAction({ nodes: formValues.nodes ?? [] })
        ? [
            {
              icon: WorkflowIcon,
              translation: errorTranslations[Errors.CAMPAIGN_EVENT_WITHOUT_ACTION],
            },
          ]
        : []

      const persistedById = new Map(
        campaign.templates
          ?.filter((template) => template.channel === ChannelEnum.Whatsapp)
          .map((template) => [template.id, template]) ?? [],
      )
      const hasActiveDefaultTemplate = formValues.templates?.some((template) => {
        const sameLanguage =
          template.language.split(/[-_]/)[0]?.toLowerCase() ===
          formValues.defaultLanguage?.split(/[-_]/)[0]?.toLowerCase()
        return sameLanguage && (persistedById.get(template.id)?.active ?? true)
      })
      const templateErrors =
        formValues.channel === ChannelEnum.Whatsapp &&
        formValues.flow === FlowEnum.Outbound &&
        formValues.defaultLanguage &&
        !hasActiveDefaultTemplate
          ? [
              {
                icon: EarthIcon,
                translation: errorTranslations[Errors.CAMPAIGN_DEFAULT_LANGUAGE_TEMPLATE_REQUIRED],
              },
            ]
          : []

      const allErrors = [...schemaErrors, ...nodeErrors, ...templateErrors]

      // Deduplicate by translation (schema refine may also produce this error in some cases)
      const seen = new Set<string>()
      const errors = allErrors.filter(({ translation }) => {
        if (seen.has(translation)) return false
        seen.add(translation)
        return true
      })

      return {
        errors,
        campaignPayload: parsed.data,
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(form.getValues()), JSON.stringify(campaign.templates)],
  ) ?? {
    errors: undefined,
    campaignPayload: undefined,
  }

  return {
    errors,
    hasErrors: Boolean(errors && errors.length > 0),
    campaignPayload,
  }
}
